// src/pages/MiCuenta.js
import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { routes } from '../routes';
import bcrypt from 'bcryptjs';

const API_BASE = 'https://ubicaciones.paginasweb.cr';

const tiposCedula = [
  { value: '01', label: 'Física' },
  { value: '02', label: 'Jurídica' },
  { value: '03', label: 'DIMEX' },
  { value: '04', label: 'NITE' },
];

const MiCuenta = ({ user, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  // Catálogos
  const [provincias, setProvincias] = useState([]); // [{code,name}]
  const [cantones, setCantones] = useState([]);
  const [distritos, setDistritos] = useState([]);

  // Perfil
  const [perfil, setPerfil] = useState({
    nombre: '',
    correo: '',
    celular: '',
  });

  // Ubicación (codes + names sincronizados)
  const [ubic, setUbic] = useState({
    provinciaCode: '',
    provinciaName: '',
    cantonCode: '',
    cantonName: '',
    distritoCode: '',
    distritoName: '',
    otras_senas: '',
  });

  // Factura electrónica
  const [requiereFE, setRequiereFE] = useState(false);
  const [fe, setFe] = useState({
    tipo_cedula: '01',
    numero_cedula: '',
    razon_social: '',
    correo_facturacion: '',
    telefono_facturacion: '',
  });

  // Password + errores por campo
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [pwdErrors, setPwdErrors] = useState({});

  // ---- Helper: prefilla cantonCode/distritoCode a partir de NOMBRES guardados ----
  const prefillCodesFromNames = async (provCode, cantonName, distritoName) => {
    try {
      if (!provCode) return;

      // Cantones
      const c = await fetch(`${API_BASE}/provincia/${provCode}/cantones.json`).then(r => r.json());
      const cantList = Object.entries(c).map(([code, name]) => ({ code, name }));
      setCantones(cantList);

      const cantonCode = (cantonName ? cantList.find(x => x.name === cantonName)?.code : '') || '';
      setUbic(prev => ({ ...prev, cantonCode }));

      if (!cantonCode) {
        setDistritos([]);
        setUbic(prev => ({ ...prev, distritoCode: '' }));
        return;
      }

      // Distritos
      const d = await fetch(`${API_BASE}/provincia/${provCode}/canton/${cantonCode}/distritos.json`).then(r => r.json());
      const distList = Object.entries(d).map(([code, name]) => ({ code, name }));
      setDistritos(distList);

      const distritoCode = (distritoName ? distList.find(x => x.name === distritoName)?.code : '') || '';
      setUbic(prev => ({ ...prev, distritoCode }));
    } catch (e) {
      console.error('prefillCodesFromNames error:', e);
    }
  };

  // ---------- Cargar provincias + datos del cliente ----------
 useEffect(() => {
  const load = async () => {
    try {
      setLoading(true);

      // 1) Cliente primero
      let data = null;
      if (user?.id) {
        const resp = await supabase.from('clientes').select('*').eq('id', user.id).single();
        if (!resp.error) data = resp.data;
      }

      // 2) Provincias (no romper si falla)
      let provList = [];
      try {
        const p = await fetch(`${API_BASE}/provincias.json`).then(r => r.json());
        provList = Object.entries(p).map(([code, name]) => ({ code, name }));
        setProvincias(provList);
      } catch (e) {
        console.warn('No se pudo cargar catálogo de provincias:', e);
      }

      if (data) {
        setPerfil({
          nombre: data?.nombre || '',
          correo: data?.correo || '',
          celular: data?.celular || '',
        });

        setRequiereFE(!!data?.requiere_fe);
        setFe({
          tipo_cedula: data?.tipo_cedula || '01',
          numero_cedula: data?.numero_cedula || '',
          razon_social: data?.razon_social || '',
          correo_facturacion: data?.correo_facturacion || '',
          telefono_facturacion: data?.telefono_facturacion || '',
        });

        const provinciaCode = provList.find(x => x.name === (data?.provincia || ''))?.code || '';
        setUbic({
          provinciaCode,
          provinciaName: data?.provincia || '',
          cantonCode: '',
          cantonName: data?.canton || '',
          distritoCode: '',
          distritoName: data?.distrito || '',
          otras_senas: data?.otras_senas || '',
        });

        // Prefill codes si hay catálogo
        if (provinciaCode) {
          await prefillCodesFromNames(provinciaCode, data?.canton, data?.distrito);
        }
      }
    } catch (e) {
      console.error(e);
      setErr('No pudimos cargar tus datos.');
    } finally {
      setLoading(false);
    }
  };
  load();
  // */ eslint-disable-next-line
}, [user?.id]);


  // ---------- Provincia cambió -> cargar cantones ----------
  useEffect(() => {
    const run = async () => {
      if (!ubic.provinciaCode) { setCantones([]); setDistritos([]); return; }
      const c = await fetch(`${API_BASE}/provincia/${ubic.provinciaCode}/cantones.json`).then(r => r.json());
      const list = Object.entries(c).map(([code, name]) => ({ code, name }));
      setCantones(list);

      // Si venía cantón por nombre desde BD y aún no hay code -> mapear
      if (ubic.cantonName && !ubic.cantonCode) {
        const found = list.find(x => x.name === ubic.cantonName);
        if (found) setUbic(prev => ({ ...prev, cantonCode: found.code }));
      } else if (!ubic.cantonCode) {
        // Si cambiaste provincia manualmente
        setUbic(prev => ({ ...prev, cantonCode: '', distritoCode: '', distritoName: '' }));
        setDistritos([]);
      }

      const provName = provincias.find(p => p.code === ubic.provinciaCode)?.name || '';
      setUbic(prev => ({ ...prev, provinciaName: provName }));
    };
    run();
 // */ eslint-disable-next-line
  }, [ubic.provinciaCode]);

  // ---------- Cantón cambió -> cargar distritos ----------
  useEffect(() => {
    const run = async () => {
      if (!ubic.provinciaCode || !ubic.cantonCode) { setDistritos([]); return; }
      const d = await fetch(`${API_BASE}/provincia/${ubic.provinciaCode}/canton/${ubic.cantonCode}/distritos.json`).then(r => r.json());
      const list = Object.entries(d).map(([code, name]) => ({ code, name }));
      setDistritos(list);

      // Mapear distrito por nombre si venía desde BD y aún no hay code
      if (ubic.distritoName && !ubic.distritoCode) {
        const found = list.find(x => x.name === ubic.distritoName);
        if (found) setUbic(prev => ({ ...prev, distritoCode: found.code }));
      } else if (!ubic.distritoCode) {
        setUbic(prev => ({ ...prev, distritoCode: '' }));
      }

      const cantonName = cantones.find(c => c.code === ubic.cantonCode)?.name || '';
      setUbic(prev => ({ ...prev, cantonName }));
    };
    run();
    // */ eslint-disable-next-line
  }, [ubic.cantonCode]);

  // Precargar distritoName cuando ya se conoce distritoCode (primer load)
  useEffect(() => {
    if (!ubic.distritoCode) return;
    const name = distritos.find(d => d.code === ubic.distritoCode)?.name || '';
    if (name && name !== ubic.distritoName) {
      setUbic(prev => ({ ...prev, distritoName: name }));
    }
  }, [ubic.distritoCode, distritos]);

  // ---------- Handlers ----------
  const onChangePerfil = (e) => {
    const { name, value } = e.target;
    setPerfil(prev => ({ ...prev, [name]: value }));
    setErr(''); setMsg('');
  };

  const onChangeFE = (e) => {
    const { name, value } = e.target;
    setFe(prev => ({ ...prev, [name]: value }));
    setErr(''); setMsg('');
  };

  const handleUbicChange = (e) => {
    const { name, value } = e.target;
    setErr(''); setMsg('');

    if (name === 'provinciaCode') {
      const provName = provincias.find(p => p.code === value)?.name || '';
      setUbic(prev => ({
        ...prev,
        provinciaCode: value,
        provinciaName: provName,
        cantonCode: '',
        cantonName: '',
        distritoCode: '',
        distritoName: '',
      }));
      return;
    }

    if (name === 'cantonCode') {
      const cantonName = cantones.find(c => c.code === value)?.name || '';
      setUbic(prev => ({
        ...prev,
        cantonCode: value,
        cantonName,
        distritoCode: '',
        distritoName: '',
      }));
      return;
    }

    if (name === 'distritoCode') {
      const distritoName = distritos.find(d => d.code === value)?.name || '';
      setUbic(prev => ({ ...prev, distritoCode: value, distritoName }));
      return;
    }

    // otras_senas
    setUbic(prev => ({ ...prev, [name]: value }));
  };

  const onPwdChange = (e) => {
    const { name, value } = e.target;
    setPwd(p => ({ ...p, [name]: value }));
    setPwdErrors(errs => ({ ...errs, [name]: '' }));
    setErr(''); setMsg('');
  };

  // ---------- Guardar perfil ----------
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setMsg(''); setErr('');

    if (!perfil.nombre.trim()) return setErr('Ingresá tu nombre.');
    if (!perfil.correo.trim()) return setErr('Ingresá tu correo.');
    if (!perfil.celular.trim()) return setErr('Ingresá tu teléfono.');

    setSaving(true);
    try {
      const payload = {
        nombre: perfil.nombre.trim(),
        correo: perfil.correo.trim(),
        celular: perfil.celular.trim(),
        provincia: ubic.provinciaName || null,
        canton: ubic.cantonName || null,
        distrito: ubic.distritoName || null,
        otras_senas: (ubic.otras_senas || '').trim() || null,
        requiere_fe: requiereFE,
        tipo_cedula: requiereFE ? fe.tipo_cedula : null,
        numero_cedula: requiereFE ? fe.numero_cedula : null,
        razon_social: requiereFE ? fe.razon_social : null,
        correo_facturacion: requiereFE ? fe.correo_facturacion : null,
        telefono_facturacion: requiereFE ? fe.telefono_facturacion || null : null,
      };

      const { error } = await supabase.from('clientes').update(payload).eq('id', user.id);
      if (error) throw error;

      setMsg('Datos guardados.');
    } catch (e2) {
      console.error(e2);
      setErr('No pudimos guardar tus datos.');
    } finally {
      setSaving(false);
    }
  };

  // ---------- Cambiar contraseña ----------
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMsg(''); setErr('');
    const errs = {};

    if (!pwd.current) errs.current = 'Ingresá tu contraseña actual.';
    if (!pwd.next) errs.next = 'Ingresá la nueva contraseña.';
    else if (pwd.next.length < 6) errs.next = 'Debe tener al menos 6 caracteres.';
    if (!pwd.confirm) errs.confirm = 'Confirmá la nueva contraseña.';
    else if (pwd.next && pwd.next !== pwd.confirm) errs.confirm = 'Las contraseñas no coinciden.';

    setPwdErrors(errs);
    if (Object.keys(errs).length) return;

    setSavingPwd(true);
    try {
      const { data, error } = await supabase
        .from('clientes')
        .select('contrasena_hash')
        .eq('id', user.id)
        .single();
      if (error) throw error;

      const ok = await bcrypt.compare(pwd.current, data?.contrasena_hash || '');
      if (!ok) {
        setPwdErrors(prev => ({ ...prev, current: 'La contraseña actual no es correcta.' }));
        return;
      }

      const newHash = await bcrypt.hash(pwd.next, 10);
      const { error: upErr } = await supabase
        .from('clientes')
        .update({ contrasena_hash: newHash })
        .eq('id', user.id);
      if (upErr) throw upErr;

      setMsg('Contraseña actualizada.');
      setPwd({ current: '', next: '', confirm: '' });
      setPwdErrors({});
    } catch (e3) {
      console.error(e3);
      setErr('No pudimos cambiar tu contraseña.');
    } finally {
      setSavingPwd(false);
    }
  };

  // ---------- Render ----------
  if (!user) {
    return (
      <div className="container mx-auto px-4 sm:px-6 py-12">
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-8 text-center">
          <h1 className="text-2xl font-bold mb-3">Debes iniciar sesión</h1>
          <p className="text-gray-600 mb-6">Iniciá sesión para administrar tu cuenta.</p>
          <button onClick={() => onNavigate(routes.login)} className="bg-brand text-white px-5 py-3 rounded-lg hover:bg-brand/90">
            Ir a login
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="container mx-auto px-4 sm:px-6 py-12">Cargando…</div>;
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-extrabold">Mi cuenta</h1>
        <span className="text-xs text-gray-500">ID cliente: {`CLI${String(user.id).padStart(4, '0')}`}</span>
      </div>

      {(msg || err) && (
        <div className={`mb-6 rounded-lg px-4 py-3 border ${msg ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {msg || err}
        </div>
      )}

      {/* Datos básicos */}
      <form onSubmit={handleSaveProfile} className="bg-white rounded-2xl shadow p-5 mb-6">
        <h2 className="text-xl font-semibold mb-4">Datos de la cuenta</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Nombre</label>
            <input name="nombre" value={perfil.nombre} onChange={onChangePerfil} className="w-full border rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Correo</label>
            <input type="email" name="correo" value={perfil.correo} onChange={onChangePerfil} className="w-full border rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Celular</label>
            <input name="celular" value={perfil.celular} onChange={onChangePerfil} className="w-full border rounded-lg px-3 py-2" />
          </div>
        </div>

        {/* Ubicación */}
        <div className="mt-6 bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold mb-3">Dirección de facturación / envío</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm text-gray-700 mb-1">Provincia</label>
              <select name="provinciaCode" value={ubic.provinciaCode} onChange={handleUbicChange} className="w-full border rounded-lg px-3 py-2 bg-white">
                <option value="">Seleccione provincia</option>
                {provincias.map(p => <option key={p.code} value={p.code}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Cantón</label>
              <select name="cantonCode" value={ubic.cantonCode} onChange={handleUbicChange} disabled={!ubic.provinciaCode} className="w-full border rounded-lg px-3 py-2 bg-white">
                <option value="">{ubic.provinciaCode ? 'Seleccione cantón' : 'Seleccione provincia'}</option>
                {cantones.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Distrito</label>
              <select name="distritoCode" value={ubic.distritoCode} onChange={handleUbicChange} disabled={!ubic.cantonCode} className="w-full border rounded-lg px-3 py-2 bg-white">
                <option value="">{ubic.cantonCode ? 'Seleccione distrito' : 'Seleccione cantón'}</option>
                {distritos.map(d => <option key={d.code} value={d.code}>{d.name}</option>)}
              </select>
            </div>
            <div className="md:col-span-1 md:col-start-1 md:col-end-5">
              <label className="block text-sm text-gray-700 mb-1">Otras señas</label>
              <input name="otras_senas" value={ubic.otras_senas} onChange={handleUbicChange} className="w-full border rounded-lg px-3 py-2" placeholder="Casa #, referencia…" />
            </div>
          </div>
        </div>

        {/* FE */}
        <div className="mt-6 bg-gray-50 rounded-lg p-4">
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={requiereFE} onChange={e => setRequiereFE(e.target.checked)} className="mt-1" />
            <div className="text-sm text-gray-800">
              Necesito Factura Electrónica
              <div className="text-xs text-gray-500">(Si estás registrado en Hacienda, marcá esta casilla)</div>
            </div>
          </label>

          {requiereFE && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Tipo de cédula</label>
                <select name="tipo_cedula" value={fe.tipo_cedula} onChange={onChangeFE} className="w-full border rounded-lg px-3 py-2 bg-white">
                  {tiposCedula.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Número de cédula</label>
                <input name="numero_cedula" value={fe.numero_cedula} onChange={onChangeFE} className="w-full border rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Razón social (opcional)</label>
                <input name="razon_social" value={fe.razon_social} onChange={onChangeFE} className="w-full border rounded-lg px-3 py-2" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-700 mb-1">Correo de facturación</label>
                <input type="email" name="correo_facturacion" value={fe.correo_facturacion} onChange={onChangeFE} className="w-full border rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Teléfono de facturación (opcional)</label>
                <input name="telefono_facturacion" value={fe.telefono_facturacion} onChange={onChangeFE} className="w-full border rounded-lg px-3 py-2" />
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={() => onNavigate(routes.home)} className="px-5 py-3 rounded-lg border hover:bg-gray-50">Cancelar</button>
          <button type="submit" disabled={saving} className="px-6 py-3 rounded-lg bg-accent hover:bg-accent/90 text-gray-900 font-semibold">
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </form>

      {/* Cambio de contraseña */}
      <form onSubmit={handleChangePassword} className="bg-white rounded-2xl shadow p-5">
        <h2 className="text-xl font-semibold mb-4">Cambiar contraseña</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Contraseña actual</label>
            <input type="password" name="current" value={pwd.current} onChange={onPwdChange} className="w-full border rounded-lg px-3 py-2" />
            {pwdErrors.current && <p className="mt-1 text-xs text-red-600">{pwdErrors.current}</p>}
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Nueva contraseña</label>
            <input type="password" name="next" value={pwd.next} onChange={onPwdChange} className="w-full border rounded-lg px-3 py-2" />
            {pwdErrors.next && <p className="mt-1 text-xs text-red-600">{pwdErrors.next}</p>}
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Confirmar contraseña</label>
            <input type="password" name="confirm" value={pwd.confirm} onChange={onPwdChange} className="w-full border rounded-lg px-3 py-2" />
            {pwdErrors.confirm && <p className="mt-1 text-xs text-red-600">{pwdErrors.confirm}</p>}
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <button type="submit" disabled={savingPwd} className="px-6 py-3 rounded-lg bg-brand text-white hover:bg-brand/90">
            {savingPwd ? 'Cambiando…' : 'Cambiar contraseña'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MiCuenta;
